// src/services/pipsCreatureKeeperTerritoryService.ts
import { logger } from '../utils/logger';



import { Territory, Creature, CreatureTerritory, GameData } from '../types/pipsCreatureKeeper';

import { unlockTerritory as unlockTerritoryDB, unlockHabitat as unlockHabitatDB } from './pipsCreatureKeeperService';



/**

 * Check and unlock territories based on captured creatures count

 */

export async function checkAndUnlockTerritories(

  gameId: string,

  gameData: GameData

): Promise<string[]> {

  const capturedCount = gameData.myCreatures.length;

  const sortedTerritories = [...gameData.territories].sort((a, b) => a.id - b.id);



  // Define unlock thresholds - matches the order of territories by ID

  const unlockThresholds = [0, 2, 4, 7, 10, 14, 18, 23, 28, 35, 42];



  const newUnlocks: string[] = [];

  const currentUnlockedCount = gameData.unlockedTerritories.length;



  // Check if we should unlock more territories

  for (let i = currentUnlockedCount; i < sortedTerritories.length && i < unlockThresholds.length; i++) {

    const requiredCaptures = unlockThresholds[i];



    if (capturedCount >= requiredCaptures) {

      const territory = sortedTerritories[i];

      await unlockTerritoryDB(gameId, territory.id);

      gameData.unlockedTerritories.push(territory.id);

      newUnlocks.push(territory.name);

    } else {

      break; // Stop checking once we hit a threshold we don't meet

    }

  }



  return newUnlocks;

}



/**

 * Check and unlock habitats based on creature count

 * Unlocks when you need more space for your creatures

 */

export async function checkAndUnlockHabitats(

  gameId: string,

  gameData: GameData

): Promise<string[]> {

  const creatureCount = gameData.myCreatures.length;

  const sortedHabitats = [...gameData.habitats].sort((a, b) => a.id - b.id);



  const newUnlocks: string[] = [];

  const currentUnlockedCount = gameData.unlockedHabitats.length;



  logger.log('Ã°Å¸Â  Checking habitat capacity:');

  logger.log(`  - Total creatures: ${creatureCount}`);

  logger.log(`  - Currently unlocked habitats: ${currentUnlockedCount}`);



  // Calculate current total capacity

  let totalCapacity = 0;

  for (let i = 0; i < currentUnlockedCount && i < sortedHabitats.length; i++) {

    totalCapacity += sortedHabitats[i].max_creatures;

    logger.log(`  - Habitat ${sortedHabitats[i].id}: ${sortedHabitats[i].max_creatures} slots`);

  }



  logger.log(`  - Total capacity: ${totalCapacity}`);

  logger.log(`  - Need: ${creatureCount} slots`);



  if (totalCapacity >= creatureCount) {

    logger.log('Ã¢Å“â€¦ Sufficient capacity - no new habitats needed');

    return newUnlocks;

  }



  logger.log(`⚠️ Need ${creatureCount - totalCapacity} more slots - unlocking habitats...`);



  // Keep unlocking habitats until we have enough space

  while (totalCapacity < creatureCount && currentUnlockedCount + newUnlocks.length < sortedHabitats.length) {

    const nextIndex = currentUnlockedCount + newUnlocks.length;

    const nextHabitat = sortedHabitats[nextIndex];



    if (nextHabitat) {

      logger.log(`🔓 Unlocking habitat ${nextHabitat.id}: ${nextHabitat.name} (+${nextHabitat.max_creatures} slots)`);

      await unlockHabitatDB(gameId, nextHabitat.id);

      gameData.unlockedHabitats.push(nextHabitat.id);

      newUnlocks.push(nextHabitat.name);

      totalCapacity += nextHabitat.max_creatures;

      logger.log(`  - New total capacity: ${totalCapacity}`);

    } else {

      break;

    }

  }



  logger.log(`Ã¢Å“â€¦ Unlocked ${newUnlocks.length} new habitats:`, newUnlocks);

  return newUnlocks;

}



/**

 * Filter creatures that can spawn in a territory

 */

export function filterCreaturesByTerritory(

  territory: Territory,

  ownedCreatureIds: Set<number>,

  gameData: GameData

): Creature[] {

  const creaturesInTerritory = gameData.creatureTerritories

    .filter(ct => ct.territory_id === territory.id)

    .map(ct => ct.creature_id);



  const availableCreatures = gameData.creatures.filter(creature =>

    !ownedCreatureIds.has(creature.id) &&

    creaturesInTerritory.includes(creature.id)

  );



  // Fallback for first territory if no creatures available

  if (availableCreatures.length === 0 && territory.id === 1) {

    return gameData.creatures

      .filter(creature => !ownedCreatureIds.has(creature.id))

      .slice(0, 10);

  }



  return availableCreatures;

}



/**

 * Get random encounter creatures for a territory

 */

export function getRandomEncounters(

  availableCreatures: Creature[],

  count: number = 3

): Creature[] {

  const shuffled = [...availableCreatures].sort(() => Math.random() - 0.5);

  return shuffled.slice(0, Math.min(count, availableCreatures.length));

}



/**

 * Get random starter creatures (monotype only)

 */

export function getRandomStarters(creatures: Creature[], count: number = 3): Creature[] {

  const monotypes = creatures.filter(creature => !creature.type2);

  const shuffled = [...monotypes].sort(() => Math.random() - 0.5);

  return shuffled.slice(0, count);

}



/**

 * Calculate next territory unlock requirements

 */

export function getNextTerritoryUnlock(

  capturedCount: number,

  gameData: GameData

): { territory: Territory | null; remaining: number } | null {

  const thresholds = [0, 2, 4, 7, 10, 14, 18, 23, 28, 35, 42];

  const allTerritories = [...gameData.territories].sort((a, b) => a.id - b.id);

  const nextIndex = gameData.unlockedTerritories.length;



  if (nextIndex >= allTerritories.length) return null;



  const nextTerritory = allTerritories[nextIndex];

  const requiredCaptures = thresholds[nextIndex] || 50;

  const remaining = Math.max(0, requiredCaptures - capturedCount);



  return {

    territory: nextTerritory,

    remaining

  };

}



/**

 * Calculate next habitat unlock requirements

 */

export function getNextHabitatUnlock(

  creatureCount: number,

  gameData: GameData

): { habitat: any | null; spacesNeeded: number } | null {

  const sortedHabitats = [...gameData.habitats].sort((a, b) => a.id - b.id);



  // Calculate current capacity

  let totalCapacity = 0;

  const unlockedCount = gameData.unlockedHabitats.length;



  for (let i = 0; i < unlockedCount && i < sortedHabitats.length; i++) {

    totalCapacity += sortedHabitats[i].max_creatures;

  }



  // If we have enough space, no unlock needed

  if (totalCapacity >= creatureCount) {

    return null;

  }



  // Get next habitat

  if (unlockedCount >= sortedHabitats.length) return null;



  const nextHabitat = sortedHabitats[unlockedCount];

  const spacesNeeded = creatureCount - totalCapacity;



  return {

    habitat: nextHabitat,

    spacesNeeded

  };

}